import { InterfaceTab, IRCMessage, IRCMessageType, tabType } from '#/types/types';
import { Channel as IRCChannel, User } from 'irc-framework';
import { getClassFromModes, parseIRCFormatting, processMessage, escapeHtml } from '#/util/helpers';
import { DEFAULT_MAX_MESSAGES_PER_TAB } from '#/constants';

export default class MessageHandler {
    private ircClient: any;
    public messageContainers: Map<string, HTMLElement>;

    constructor(ircClient: any) {
        this.ircClient = ircClient;
        this.messageContainers = new Map();
    }

    addMessage(tab: InterfaceTab, message: string, type: IRCMessageType, sender?: string): void {
        sender = sender || '';
        let messagesElement = this.messageContainers.get(tab.name);
        if (!messagesElement) {
            messagesElement = document.querySelector(`.irc-messages[data-tab-id="${tab.key}"]`) as HTMLElement;
            if (messagesElement) {
                this.messageContainers.set(tab.name, messagesElement);
            }
        }
        
        if (!messagesElement) return;

        const messageElement = document.createElement('div');
        messageElement.className = 'irc-message';

        const { text, urls } = processMessage(message);
        const parsedMessage = parseIRCFormatting(text).replace(/__URL_PLACEHOLDER_(\d+)__/g, (match, indexStr) => {
            const index = parseInt(indexStr);
            const url = urls[index];
            const escapedUrl = escapeHtml(url);
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${escapedUrl}</a>`;
        });
        
        let formattedMessage = '';
        const timestamp = new Date();
        
        switch (type) {
            case IRCMessageType.ERROR:
                formattedMessage = `<span class="error-message">[ERROR] ${parsedMessage}</span>`;
                break;
            case IRCMessageType.SYSTEM: // Client generated messages, not the server
                formattedMessage = `<span class="system-message">${parsedMessage}</span>`;
                break;
            case IRCMessageType.WHOIS:
                formattedMessage = `<span class="whois-message">${parsedMessage}</span>`;
                break;
            case IRCMessageType.NOTICE:
                if (sender.length === 0) {
                    formattedMessage = `<span class="irc-notice">-> ${parsedMessage}</span>`;
                } else {
                    formattedMessage = `<span class="irc-notice">-${sender}- ${parsedMessage}</span>`;
                }
                break;
            case IRCMessageType.ACTION:
                const actionUser = tab.users?.find(user => user.nick === sender);

                const actionUserModes = actionUser?.modes;
                let modeClass;
                if (actionUserModes) {
                    modeClass = getClassFromModes(actionUserModes);
                }

                const actionClassAttr = modeClass ? ` ${modeClass}` : '';
                const newMessage = message.split(' ').slice(2).join(' ');
                console.log(message);
                console.log(newMessage);
                const { text: actionText, urls: actionUrls } = processMessage(newMessage);
                const formattedActionMessage = parseIRCFormatting(actionText).replace(/__URL_PLACEHOLDER_(\d+)__/g, (match, indexStr) => {
                    const index = parseInt(indexStr);
                    const url = actionUrls[index];
                    const escapedUrl = escapeHtml(url);
                    return `<a href="${url}" target="_blank" rel="noopener noreferrer">${escapedUrl}</a>`;
                });
                formattedMessage = `<span class="action">* <span class="irc-sender${actionClassAttr}">${sender} ${formattedActionMessage}</span></span>`;
                break;
            case IRCMessageType.DEFAULT:
            default:
                if (sender) {
                    const messageUser = tab.users?.find(user => user.nick === sender);

                    const messageUserModes = messageUser?.modes;
                    let modeClass;
                    if (messageUserModes) {
                        modeClass = getClassFromModes(messageUserModes);
                    }

                    const classAttr = modeClass ? ` class="irc-sender ${modeClass}"` : ' class="irc-sender"';
                    formattedMessage = `<span${classAttr}>${sender}:</span> ${parsedMessage}`;
                } else {
                    formattedMessage = `${parsedMessage}`;
                }
                break;
        }
        
        messageElement.innerHTML = `<span class="timestamp">[${timestamp.toLocaleTimeString()}]</span> ${formattedMessage}`;
        messagesElement.appendChild(messageElement);
        messagesElement.scrollTop = messagesElement.scrollHeight;

        const ircMessage: IRCMessage = {
            timestamp: timestamp,
            type: type,
            sender: sender || undefined,
            content: message
        };
        tab.messages.push(ircMessage);

        this.cullOldMessages(tab, messagesElement);
    }

    private cullOldMessages(tab: InterfaceTab, messagesElement: HTMLElement): void {
        while (tab.messages.length > DEFAULT_MAX_MESSAGES_PER_TAB) {
            tab.messages.shift();
            const messageElements = messagesElement.querySelectorAll('.irc-message');
            if (messageElements.length > 0) {
                messageElements[0].remove();
            }
        }
    }

    sendMessage(tab: InterfaceTab, message: string): void {
        if (tab.type === tabType.STATUS) {
            if (!this.ircClient.isConnected) {
                this.addMessage(tab, 'You are not connected to an IRC server. Please check the Settings tab to connect.', IRCMessageType.ERROR);
                return;
            }
            
            if (message.startsWith('/')) {
                this.ircClient.commandHandler.handleCommand(tab, message);
            } else {
                this.ircClient.connectionHandler.sendRaw(message);
            }
            return;
        }

        if (message && message.startsWith('/')) {
            this.ircClient.commandHandler.handleCommand(tab, message);
            return;
        }

        const nicknameInput = document.getElementById('irc-nickname') as HTMLInputElement;
        const nickname = nicknameInput?.value || 'Unknown';
        this.addMessage(tab, message, IRCMessageType.DEFAULT, nickname);
        
        this.ircClient.connectionHandler.sendMessage(tab.name, message);
    }

    updateChannelList(channels: IRCChannel[]): void {
        const channelListContent = document.querySelector('.irc-channel-list-container');
        if (!channelListContent) return;

        channelListContent.innerHTML = '';

        channels.forEach(channel => {
            const channelElement = document.createElement('div');
            channelElement.className = 'irc-channel-list-item';

            const nameElement = document.createElement('span');
            nameElement.className = 'channel-name';
            nameElement.textContent = channel.name;

            const usersElement = document.createElement('span');
            usersElement.className = 'channel-users';
            usersElement.textContent = `${channel.users} users`;

            const topicElement = document.createElement('span');
            topicElement.className = 'channel-topic';
            topicElement.textContent = channel.topic || 'No topic';

            channelElement.appendChild(nameElement);
            channelElement.appendChild(usersElement);
            channelElement.appendChild(topicElement);

            channelListContent.appendChild(channelElement);
        });
    }

    updateUserList(tab: InterfaceTab, users: (User)[]): void {
        if (!tab || tab.type !== tabType.CHANNEL) {
            return;
        }
        tab.users = users;
        const userListContent = document.querySelector(`.irc-userlist-content[data-tab-id="${tab.key}"]`) as HTMLElement;
        if (!userListContent) {
            setTimeout(() => {
                const retryElement = document.querySelector(`.irc-userlist-content[data-tab-id="${tab.key}"]`) as HTMLElement;
                if (retryElement) {
                    this.populateUserList(retryElement, users, tab);
                }
            }, 100);
            return;
        }

        this.populateUserList(userListContent, users, tab);
    }

    private getPrimaryMode(modes: string[]): string {
        const modeOrder = ['q', 'a', 'o', 'h', 'v'];
        for (const mode of modeOrder) {
            if (modes.includes(mode)) {
                return mode;
            }
        }
        return '';
    }

    populateUserList(userListContent: HTMLElement, users: User[], tab: InterfaceTab): void {
        userListContent.innerHTML = '';

        const groupedUsers: { [key: string]: User[] } = {
            'q': [], // Owner
            'a': [], // Admin
            'o': [], // Op
            'h': [], // Halfop
            'v': [], // Voice
            '': []   // Regular users
        };

        users.forEach((user: User) => {
            if (!user) return;
            let primaryMode = '';
            if (user.modes && user.modes.length > 0) {
                primaryMode = this.getPrimaryMode(user.modes);
            }
            groupedUsers[primaryMode].push(user);
        });

        Object.keys(groupedUsers).forEach(group => {
            groupedUsers[group].sort((a, b) => {
                return a.nick.localeCompare(b.nick);
            });
        });

        const hierarchyOrder = ['q', 'a', 'o', 'h', 'v', ''];

        hierarchyOrder.forEach(group => {
            groupedUsers[group].forEach(user => {
                const userElement = document.createElement('div');
                userElement.className = 'irc-user';

                let modeClass;
                if (user.modes && user.modes.length > 0) {
                    modeClass = getClassFromModes(user.modes);
                }

                if (modeClass) {
                    userElement.classList.add(modeClass);
                }

                userElement.textContent = user.nick;
                userElement.dataset.username = user.nick;
                userElement.dataset.channel = tab.name;
                userElement.dataset.mask = user.hostname ? `${user.nick}!${user.username}@${user.hostname}` : user.nick;

                userElement.addEventListener('dblclick', () => {
                    this.ircClient.commandHandler.openQueryWindow(user.nick);
                });

                userElement.addEventListener('contextmenu', (e) => {
                    e.preventDefault();
                    this.showUserContextMenu(e, user.nick, tab.name, user.hostname ? `${user.nick}!${user.username}@${user.hostname}` : user.nick);
                });

                userListContent.appendChild(userElement);
            });
        });
    }

    showUserContextMenu(event: MouseEvent, username: string, channel: string, userMask: string): void {
        const existingMenu = document.querySelector('.irc-context-menu');
        if (existingMenu) {
            existingMenu.remove();
        }
        
        const menu = document.createElement('div');
        menu.className = 'irc-context-menu';
        menu.style.position = 'absolute';
        menu.style.left = event.pageX + 'px';
        menu.style.top = event.pageY + 'px';
        menu.style.zIndex = '1000';
        
        const queryItem = this.createMenuItem('Query', () => {
            this.ircClient.commandHandler.openQueryWindow(username);
            menu.remove();
        });
        menu.appendChild(queryItem);
        

        const whoisItem = this.createMenuItem('Whois', () => {
            this.ircClient.connectionHandler.sendIRCCommand('WHOIS', username);
            menu.remove();
        });
        menu.appendChild(whoisItem);

        document.body.appendChild(menu);
        
        const closeMenu = (e: Event) => {
            if (!menu.contains(e.target as Node)) {
                menu.remove();
                document.removeEventListener('click', closeMenu);
            }
        };
        setTimeout(() => document.addEventListener('click', closeMenu), 10);
    }
    
    createMenuItem(text: string, callback: () => void): HTMLElement {
        const item = document.createElement('div');
        item.className = 'irc-context-menu-item';
        item.textContent = text;
        item.addEventListener('click', callback);
        return item;
    }
}